import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template6Props {
  data: ResumeData;
}

export const Template6: React.FC<Template6Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      <div className="grid grid-cols-4 gap-0">
        {/* Left Sidebar */}
        <div className="col-span-1 bg-slate-800 text-white p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">{data.personalInfo.name}</h1>
            <h2 className="text-sm opacity-80 uppercase tracking-wide">{data.personalInfo.title}</h2>
          </div>

          {/* Contact */}
          <div className="mb-8">
            <h3 className="text-xs font-bold mb-4 uppercase tracking-wider opacity-80">
              Contact
            </h3>
            <div className="space-y-2 text-xs">
              <p>{data.personalInfo.email}</p>
              <p>{data.personalInfo.phone}</p>
              <p>{data.personalInfo.location}</p>
              {data.personalInfo.linkedin && <p>{data.personalInfo.linkedin}</p>}
              {data.personalInfo.website && <p>{data.personalInfo.website}</p>}
            </div>
          </div>

          {/* Skills */}
          <div className="mb-8">
            <h3 className="text-xs font-bold mb-4 uppercase tracking-wider opacity-80">
              Skills
            </h3>
            <div className="space-y-3">
              {data.skills.map((skill, index) => (
                <div key={index}>
                  <p className="text-xs mb-1">{skill}</p>
                  <div className="w-full bg-slate-700 h-1 rounded">
                    <div className="bg-cyan-400 h-1 rounded w-4/5"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Education */}
          <div>
            <h3 className="text-xs font-bold mb-4 uppercase tracking-wider opacity-80">
              Education
            </h3>
            <EducationFormatter 
              education={data.education} 
              className="text-white text-xs"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-3 p-8">
          {/* Summary */}
          <section className="mb-8">
            <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b-2 border-cyan-400">
              PROFESSIONAL SUMMARY
            </h3>
            <p className="text-gray-700 leading-relaxed">{data.summary}</p>
          </section>

          {/* Experience */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-6 pb-2 border-b-2 border-cyan-400">
              PROFESSIONAL EXPERIENCE
            </h3>
            
            {data.experience.map((job, index) => (
              <div key={index} className="mb-8">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-slate-800">{job.title}</h4>
                    <p className="text-cyan-600 font-semibold">{job.company}</p>
                    <p className="text-sm text-gray-600">{job.location}</p>
                  </div>
                  <div className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm font-medium">
                    {job.dates}
                  </div>
                </div>
                
                <ul className="space-y-2">
                  {job.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};